import React, { useEffect, useState, useCallback } from 'react';
import { Brain, TrendingUp, Users, Activity, Plus, Target } from 'lucide-react';
import {
  getWorkspaceJudgmentInsights,
  getRecentSignals,
  getRecentObservations,
  getMyJudgmentProfiles,
} from '../lib/decisionIntelligenceService';
import { buildJudgmentSummary, TIER_META } from '../lib/judgmentScoringEngine';
import type {
  JudgmentProfile,
  DecisionSignal,
  OutcomeObservation,
  WorkspaceJudgmentInsights,
} from '../types/decisionIntelligence';
import { WhoseVoiceMatters } from '../components/decision-intelligence/WhoseVoiceMatters';
import { JudgmentProfileCard } from '../components/decision-intelligence/JudgmentProfileCard';
import { DecisionSignalCapture } from '../components/decision-intelligence/DecisionSignalCapture';
import { OutcomeObservationPanel } from '../components/decision-intelligence/OutcomeObservationPanel';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import { useUserStore } from '../stores/prioritization/userStore';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';

type Tab = 'voice' | 'feed' | 'my_track_record';

const OUTCOME_EMOJI: Record<string, string> = {
  shipped:   '🚀',
  succeeded: '✅',
  partial:   '🔶',
  delayed:   '⏳',
  pivoted:   '↩️',
  cancelled: '❌',
  failed:    '💥',
};

const RECOMMENDATION_COLOR: Record<string, string> = {
  proceed: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  modify:  'text-[var(--mustard)] dark:text-[var(--mustard)]',
  pause:   'text-[var(--mustard)] dark:text-[var(--mustard)]',
  stop:    'text-[var(--coral)] dark:text-[var(--coral)]',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatCard({ icon: Icon, label, value, sub }: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 flex items-center gap-3">
      <div className="bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] p-2.5 shrink-0">
        <Icon className="w-4 h-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] tabular-nums">{value}</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{label}</p>
        {sub && <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SignalRow({ signal, actorName, itemTitle, onLogOutcome }: {
  signal: DecisionSignal;
  actorName?: string;
  itemTitle?: string;
  /** Log the outcome for this signal's item. Absent once it is scored, or with no item. */
  onLogOutcome?: () => void;
}) {
  const rc    = RECOMMENDATION_COLOR[signal.recommendation] ?? 'text-[var(--text-ter)] dark:text-[var(--text-sec)]';
  const name  = actorName ?? (signal.actorEmail
    ? signal.actorEmail.split('@')[0].replace(/[._-]/g, ' ')
    : signal.actorId.slice(0, 8));
  const hasAttribution = signal.attribution != null;

  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors group">
      {/* Confidence bubble */}
      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 tabular-nums ${
        signal.confidenceScore >= 8 ? 'border-[var(--green)] text-[var(--green)] dark:border-[var(--green)] dark:text-[var(--text-ter)]' :
        signal.confidenceScore >= 5 ? 'border-[var(--mustard)] text-[var(--mustard)] dark:border-[var(--mustard)] dark:text-[var(--mustard)]' :
                                      'border-[var(--coral)] text-[var(--coral)] dark:border-[var(--coral)] dark:text-[var(--coral)]'
      }`}>
        {signal.confidenceScore}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] capitalize">{name}</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${rc}`}>{signal.recommendation}</span>
          <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 rounded-full">{signal.domain}</span>
          {hasAttribution && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              signal.attribution!.outcomeAligned
                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]'
                : 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]'
            }`}>
              {signal.attribution!.outcomeAligned ? '✓ aligned' : '✗ misaligned'}
            </span>
          )}
        </div>
        {itemTitle && (
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-pri)] mt-0.5 truncate">{itemTitle}</p>
        )}
        {signal.rationale && (
          <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5 line-clamp-2">{signal.rationale}</p>
        )}
        <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">{formatDate(signal.createdAt)}</p>
      </div>
      {onLogOutcome && (
        <button
          type="button"
          onClick={onLogOutcome}
          className="shrink-0 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] underline-offset-2 hover:underline"
        >
          Log outcome
        </button>
      )}
    </div>
  );
}

function ObservationRow({ obs, itemTitle }: { obs: OutcomeObservation; itemTitle?: string }) {
  const emoji = OUTCOME_EMOJI[obs.outcomeType] ?? '•';
  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors">
      <span className="text-lg shrink-0 leading-none mt-0.5">{emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] capitalize">{obs.outcomeType}</span>
          {obs.outcomeQualityScore && (
            <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)]">quality: <span className="text-[var(--text-sec)] dark:text-[var(--text-pri)]">{obs.outcomeQualityScore}/10</span></span>
          )}
          {obs.executionSource && (
            <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 rounded-full">{obs.executionSource}</span>
          )}
        </div>
        {itemTitle && (
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-pri)] mt-0.5 truncate">{itemTitle}</p>
        )}
        {obs.notes && (
          <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5 line-clamp-2">{obs.notes}</p>
        )}
        <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-1">{formatDate(obs.observedAt)}</p>
      </div>
    </div>
  );
}

function MyTrackRecord({ profiles }: { profiles: JudgmentProfile[] }) {
  if (profiles.length === 0) {
    return (
      <div className="text-center py-12">
        <Brain className="w-10 h-10 text-[var(--text-ter)] dark:text-[var(--text-dim)] mx-auto mb-3" />
        <p className="text-[var(--text-ter)] dark:text-[var(--text-sec)] text-sm font-medium">No track record yet</p>
        <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs mt-1 max-w-xs mx-auto">
          Start capturing decision signals. Once outcomes are recorded, your judgment profile will appear here.
        </p>
      </div>
    );
  }

  const topProfile = profiles.reduce((best, p) =>
    (p.calibrationScore ?? 0) > (best.calibrationScore ?? 0) ? p : best
  );
  const summary = buildJudgmentSummary(topProfile);
  const meta    = TIER_META[summary.tier];

  return (
    <div className="space-y-6">
      {/* Summary hero */}
      <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]/30 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 rounded-full p-2.5">
            <Brain className="w-5 h-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Your Judgment Profile</p>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
          </div>
        </div>

        {summary.strengths.length > 0 && (
          <div className="space-y-1.5">
            {summary.strengths.map((s, i) => (
              <p key={i} className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex gap-2">
                <span className="shrink-0 mt-0.5">✓</span>{s}
              </p>
            ))}
          </div>
        )}
        {summary.cautions.length > 0 && (
          <div className="space-y-1.5 mt-2">
            {summary.cautions.map((c, i) => (
              <p key={i} className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] flex gap-2">
                <span className="shrink-0 mt-0.5">△</span>{c}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* All domain profiles */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-3">By Domain</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {profiles.map(p => (
            <JudgmentProfileCard key={`${p.userId}-${p.domain}`} profile={p} showEmail={false} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] dark:border-[var(--rule)] w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)] text-lg leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function DecisionIntelligence() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const getItemById = useItemsStore((s) => s.getItemById);
  const { members } = useWorkspaceMembers(workspaceId);
  const [tab, setTab] = useState<Tab>('voice');
  const [insights, setInsights] = useState<WorkspaceJudgmentInsights | null>(null);
  const [signals, setSignals] = useState<DecisionSignal[]>([]);
  const [observations, setObservations] = useState<OutcomeObservation[]>([]);
  const [myProfiles, setMyProfiles] = useState<JudgmentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  // The item a signal's "Log outcome" opened the form on; null lets the person pick one.
  const [outcomeItemId, setOutcomeItemId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [ins, sigs, obs, mine] = await Promise.all([
        getWorkspaceJudgmentInsights(workspaceId),
        getRecentSignals(workspaceId, 30),
        getRecentObservations(workspaceId, 20),
        getMyJudgmentProfiles(workspaceId),
      ]);
      setInsights(ins);
      setSignals(sigs);
      setObservations(obs);
      setMyProfiles(mine);
    } catch (_) {
      // silently fail — workspace may not have data yet
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const openOutcomeModal = (itemId: string | null) => {
    setOutcomeItemId(itemId);
    setShowOutcomeModal(true);
  };
  const closeOutcomeModal = () => {
    setShowOutcomeModal(false);
    setOutcomeItemId(null);
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'voice',           label: "Whose Voice Matters",  icon: Users      },
    { id: 'feed',            label: 'Decision Feed',         icon: Activity   },
    { id: 'my_track_record', label: 'My Track Record',       icon: TrendingUp },
  ];

  const totalSignals       = insights?.totalSignals       ?? 0;
  const totalObservations  = insights?.totalObservations  ?? 0;
  const totalProfiles      = insights?.profiles.length    ?? 0;
  const resolvedRate = totalSignals > 0
    ? Math.round((totalObservations / totalSignals) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <Brain className="w-5 h-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              <h1 className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Decision Intelligence</h1>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] max-w-lg">
              Track confidence signals, observe outcomes, and surface whose judgment to trust — by domain, over time.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => openOutcomeModal(null)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[var(--rule)] text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:border-[var(--rule-bright)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Target className="w-3.5 h-3.5" />
              Log Outcome
            </button>
            <button
              onClick={() => setShowSignalModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[var(--slate)] hover:bg-[var(--slate)] text-white font-semibold transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Capture Signal
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Activity}   label="Decision signals"   value={totalSignals}      />
          <StatCard icon={Target}     label="Outcomes logged"    value={totalObservations} />
          <StatCard icon={TrendingUp} label="Resolution rate"    value={`${resolvedRate}%`} sub="signals with outcomes" />
          <StatCard icon={Users}      label="Scored forecasters" value={totalProfiles}     sub="≥3 resolved decisions" />
        </div>

        {/* Tabs */}
        <div className="border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="flex gap-1">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                  tab === t.id
                    ? 'border-[var(--slate)] text-[var(--text-ter)] dark:border-[var(--slate)] dark:text-[var(--text-ter)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {loading ? (
          <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)] text-sm">Loading…</div>
        ) : (
          <>
            {tab === 'voice' && (
              <div>
                {insights && insights.profiles.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-10 h-10 text-[var(--text-ter)] dark:text-[var(--text-dim)] mx-auto mb-3" />
                    <p className="text-[var(--text-ter)] dark:text-[var(--text-sec)] text-sm font-medium">No judgment profiles yet</p>
                    <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs mt-1 max-w-sm mx-auto">
                      Profiles appear once team members have captured signals that were later matched to outcomes.
                      Start by capturing a decision signal.
                    </p>
                  </div>
                ) : (
                  <WhoseVoiceMatters
                    profiles={insights?.profiles ?? []}
                    currentUserId={userId}
                  />
                )}
              </div>
            )}

            {tab === 'feed' && (
              <div className="space-y-6">
                {/* Signals */}
                <div>
                  <h3 className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5" />
                    Recent Decision Signals
                  </h3>
                  {signals.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-[var(--rule)] dark:border-[var(--rule)]">
                      <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-sm">No signals yet.</p>
                      <button onClick={() => setShowSignalModal(true)} className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs mt-1 hover:underline">
                        Capture the first signal →
                      </button>
                    </div>
                  ) : (
                    <div className="bg-[var(--surface)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                      {signals.map(s => (
                        <SignalRow
                          key={s.id}
                          signal={s}
                          actorName={members.find((m) => m.userId === s.actorId)?.name}
                          itemTitle={s.itemId ? getItemById(s.itemId)?.title : undefined}
                          onLogOutcome={s.itemId && !s.attribution ? () => openOutcomeModal(s.itemId) : undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Observations */}
                <div>
                  <h3 className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Target className="w-3.5 h-3.5" />
                    Recent Outcomes
                  </h3>
                  {observations.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-[var(--rule)] dark:border-[var(--rule)]">
                      <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-sm">No outcomes logged yet.</p>
                      <button onClick={() => openOutcomeModal(null)} className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs mt-1 hover:underline">
                        Log the first outcome →
                      </button>
                    </div>
                  ) : (
                    <div className="bg-[var(--surface)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                      {observations.map(o => (
                        <ObservationRow key={o.id} obs={o} itemTitle={o.itemId ? getItemById(o.itemId)?.title : undefined} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'my_track_record' && (
              <MyTrackRecord profiles={myProfiles} />
            )}
          </>
        )}
      </div>

      {/* Signal capture modal */}
      {showSignalModal && (
        <Modal title="Capture Decision Signal" onClose={() => setShowSignalModal(false)}>
          <DecisionSignalCapture
            workspaceId={workspaceId ?? ''}
            onCaptured={() => { setShowSignalModal(false); load(); }}
            onCancel={() => setShowSignalModal(false)}
          />
        </Modal>
      )}

      {/* Outcome modal */}
      {showOutcomeModal && (
        <Modal title="Log Outcome" onClose={closeOutcomeModal}>
          <OutcomeObservationPanel
            workspaceId={workspaceId ?? ''}
            itemId={outcomeItemId ?? undefined}
            itemTitle={outcomeItemId ? getItemById(outcomeItemId)?.title : undefined}
            onRecorded={() => { closeOutcomeModal(); load(); }}
            onCancel={closeOutcomeModal}
          />
        </Modal>
      )}
    </div>
  );
}
